/**
 * Что лежит в очереди и почему оно там застряло.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠ ЗАЧЕМ ЭТО НУЖНО: ОЧЕРЕДЬ УМЕЕТ ВСТАТЬ НАВСЕГДА И МОЛЧА
 * ─────────────────────────────────────────────────────────────────────────
 * Досылка строго последовательна — «завершить» не должно обгонять «взять» по
 * той же задаче. Из этого следует, что первая же запись, которую нельзя
 * отправить, держит все остальные.
 *
 * Отказ по существу (4xx) записи не задерживает: она уходит из очереди с
 * тостом. А вот `retry` — сетевой сбой, 5xx и, главное, 401 после неудачного
 * обновления сессии — оставляет запись на месте и не показывает ничего.
 * До этой панели скаут видел только несжимающийся счётчик «N действий ждут
 * связи»: ни причины, ни способа снять застрявшее. Единственным выходом был
 * «Выйти», который стирает очередь целиком, вместе с чужой невиновной работой.
 *
 * Панель не чинит саму блокировку — она делает её видимой и разрешает снять
 * ровно одну запись, ту, что мешает.
 */
import { useState } from 'react'
import { dropEntry, type OutboxEntry } from '@/lib/offline/outbox'
import { timeAgo } from '@/lib/geo'

/** Человеческое имя действия. Тот же словарь, что в текстах отказов. */
function actionName(entry: OutboxEntry): string {
  switch (entry.kind) {
    case 'claim':
      return 'Взять задачу'
    case 'complete':
      return 'Завершить задачу'
    case 'create_pickup':
      return 'Задача «Забрать»'
    case 'create_rebalance':
      return 'Задача «Переставить»'
    case 'vehicle_report':
      return 'Замечание диспетчеру'
    case 'label_attach':
      return 'Добавить лейбл'
    case 'label_detach':
      return 'Снять лейбл'
    case 'vehicle_status':
      return `Статус: ${String(entry.payload.action ?? '—')}`
    case 'pool_pick':
      return 'Пул: забрал'
    case 'pool_deliver':
      return 'Пул: поставил'
    case 'pool_drop':
      return 'Пул: убрать'
    case 'collector_cancel':
      return 'Сборщик: отменить задание'
  }
}

/**
 * Почему запись ещё здесь.
 *
 * Различие важнее формулировки: «не дошло до сервера» лечится ожиданием и
 * само, «сервер отказал» — нет. `attempts === 0` означает, что до записи
 * очередь просто не дошла: её держит предыдущая.
 */
function whyStuck(entry: OutboxEntry, isHead: boolean): string {
  if (!isHead && entry.attempts === 0) return 'ждёт очереди'
  if (entry.lastStatus === 401) return 'сессия истекла — войдите заново'
  if (entry.lastError) return entry.lastError
  return 'не дошло до сервера — повторим сами'
}

export function OutboxPanel({ entries }: { entries: readonly OutboxEntry[] }) {
  const [open, setOpen] = useState(false)
  if (entries.length === 0) return null

  const blocked = entries.length - 1

  return (
    <div className="mb-4 rounded-lg bg-amber-500/15 text-sm text-amber-200">
      <button
        className="flex min-h-11 w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v)
        }}
      >
        <span>
          {entries.length} действ{entries.length === 1 ? 'ие ждёт' : 'ия ждут'} связи — отправим
          сами
        </span>
        <span className="ml-3 shrink-0 underline">{open ? 'Скрыть' : 'Показать'}</span>
      </button>

      {open ? (
        <ul className="border-t border-amber-200/20">
          {entries.map((entry, i) => {
            const isHead = i === 0
            return (
              <li
                key={entry.id}
                className="flex items-start justify-between gap-3 border-b border-amber-200/10
                  px-4 py-3 last:border-0"
              >
                <div className="min-w-0">
                  <div className="font-medium">{actionName(entry)}</div>
                  <div className="mt-0.5 opacity-80">{whyStuck(entry, isHead)}</div>
                  <div className="mt-0.5 text-xs opacity-60">
                    {timeAgo(entry.queuedAt) ?? 'только что'}
                    {entry.attempts > 0 ? ` · попыток: ${entry.attempts}` : ''}
                  </div>
                  {/*
                    Голову очереди называем прямо: без этого непонятно, почему
                    остальные висят, и скаут будет снимать не ту запись.
                  */}
                  {isHead && blocked > 0 ? (
                    <div className="mt-1 text-xs font-medium">
                      держит остальные: {blocked}
                    </div>
                  ) : null}
                </div>
                <button
                  className="min-h-11 shrink-0 underline"
                  onClick={() => {
                    void dropEntry(entry.id)
                  }}
                >
                  Снять
                </button>
              </li>
            )
          })}
          <li className="px-4 py-3 text-xs opacity-70">
            «Снять» отменяет действие на этом телефоне. На сервер оно не уйдёт —
            если оно всё ещё нужно, сделайте его заново.
          </li>
        </ul>
      ) : null}
    </div>
  )
}
